import React from 'react';
import { CSPUrlsMap } from '../../routes/CSPUrlsMap';
import { UPLOAD_DOCUMENTS_FORM_KEYS } from '../../constants/formKeys';
import CheckBoxInput from '../../components/shared/CheckBoxInput/CheckBoxInput';
import DocTypeFileInput from './docTypeFileInput/docTypeFileInput';

const STATUS_CODES = {
  INVOICE: 'INVOICE',
  CLIENT_CORRESPONDENCE: 'CCORRES',
  CLAIM_FORM: 'CFORM',
  VET_CONSULTATION_NOTES: 'CNNOTES',
  PATHOLOGY_REPORT: 'PATH',
  PRE_AUTHORIZATION: 'PREAUTH',
  PURCHASE_RECEIPT: 'PPURCHASE',
  RECEIPT: 'RECEIPT',
  RADIOLOGY_REPORT: 'RTG',
  VET_HISTORY: 'VETHIST',
  OTHER: 'OTHER'
};

const STATUS_ORDER = [
  STATUS_CODES.INVOICE,
  STATUS_CODES.CLIENT_CORRESPONDENCE,
  STATUS_CODES.CLAIM_FORM,
  STATUS_CODES.VET_CONSULTATION_NOTES,
  STATUS_CODES.PATHOLOGY_REPORT,
  STATUS_CODES.PURCHASE_RECEIPT,
  STATUS_CODES.PRE_AUTHORIZATION,
  STATUS_CODES.RECEIPT,
  STATUS_CODES.RADIOLOGY_REPORT,
  STATUS_CODES.VET_HISTORY,
  STATUS_CODES.OTHER
];

// Sort and filter missingDocsStatuses according to statusOrder
export const sortedMissingDocsStatuses = (missingDocsStatuses) =>
  STATUS_ORDER.filter((status) => missingDocsStatuses.includes(status));

export const mapUploadFileInfo = (
  formContext,
  statusCode,
  date,
  petName,
  isUploading,
  additionalAttachementsByType,
  onUploadingChange
) => {
  const noComment = !formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.COMMENTS);
  const noInvoice =
    formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.INVOICE) &&
    formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.INVOICE).length === 0;

  switch (statusCode) {
    case STATUS_CODES.CLIENT_CORRESPONDENCE:
      return (
        <>
          <h3>Upload client correspondence for {date}</h3>
          <DocTypeFileInput
            formKey={UPLOAD_DOCUMENTS_FORM_KEYS.CLIENT_CORRESPONDENCE}
            selectedFiles={additionalAttachementsByType[UPLOAD_DOCUMENTS_FORM_KEYS.CLIENT_CORRESPONDENCE]}
            isRequired={noComment && formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.CLIENT_CORRESPONDENCE)?.length === 0}
            isUploading={isUploading}
            onUploadingChange={onUploadingChange}
          />
        </>
      );
    case STATUS_CODES.CLAIM_FORM:
      return (
        <>
          <h3>Upload your claim form for {date}</h3>
          <p>A claim form is a form completed by you and your vet summarising what the claim is for.</p>
          <a
            href="https://petcdnstore.blob.core.windows.net/csp-images/Templates/ABC_InsuranceClaim_v2.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            See an example
          </a>
          <DocTypeFileInput
            formKey={UPLOAD_DOCUMENTS_FORM_KEYS.CLAIM_FORM}
            selectedFiles={additionalAttachementsByType[UPLOAD_DOCUMENTS_FORM_KEYS.CLAIM_FORM]}
            isRequired={noComment && formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.CLAIM_FORM)?.length === 0}
            isUploading={isUploading}
            onUploadingChange={onUploadingChange}
          />
        </>
      );

    case STATUS_CODES.VET_CONSULTATION_NOTES:
      return (
        <>
          <h3>Upload your vet consultation notes for {date}</h3>
          <>
            <p>
              This includes medical records documented by your vet for the specified date/s. You will need to contact
              your vet directly to obtain these records.
            </p>
            <a
              href="https://petcdnstore.blob.core.windows.net/csp-images/Templates/ABC_Consult%20notes_v2.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              See an example
            </a>
            <DocTypeFileInput
              formKey={UPLOAD_DOCUMENTS_FORM_KEYS.VET_CONSULTATION_NOTES}
              selectedFiles={additionalAttachementsByType[UPLOAD_DOCUMENTS_FORM_KEYS.VET_CONSULTATION_NOTES]}
              isRequired={
                noComment && formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.VET_CONSULTATION_NOTES)?.length === 0
              }
              isUploading={isUploading}
              onUploadingChange={onUploadingChange}
            />
          </>
        </>
      );

    case STATUS_CODES.PATHOLOGY_REPORT:
      return (
        <>
          <h3>Upload your pathology report for {date}</h3>
          <p>
            This refers to the results of tests performed by your veterinarian to investigate your pet’s condition.
            Examples of these are blood tests or tissue analysis. You will need to contact your vet directly to obtain
            these reports.
          </p>
          <a
            href="https://petcdnstore.blob.core.windows.net/csp-images/Templates/ABC_VetPatholgy_v2.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            See an example
          </a>
          <DocTypeFileInput
            formKey={UPLOAD_DOCUMENTS_FORM_KEYS.PATHOLOGY_REPORT}
            selectedFiles={additionalAttachementsByType[UPLOAD_DOCUMENTS_FORM_KEYS.PATHOLOGY_REPORT]}
            isRequired={noComment && formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.PATHOLOGY_REPORT)?.length === 0}
            requiredMessage="Please upload at least one pathology report or add a comment below."
            isUploading={isUploading}
            onUploadingChange={onUploadingChange}
          />
        </>
      );

    case STATUS_CODES.PRE_AUTHORIZATION:
      return (
        <>
          <h3>Upload your pre-authorisation form for {date}</h3>
          <DocTypeFileInput
            formKey={UPLOAD_DOCUMENTS_FORM_KEYS.PRE_AUTHORIZATION}
            selectedFiles={additionalAttachementsByType[UPLOAD_DOCUMENTS_FORM_KEYS.PRE_AUTHORIZATION]}
            isRequired={noComment && formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.PRE_AUTHORIZATION)?.length === 0}
            requiredMessage="Please upload a pre-authorisation form or add a comment below."
            isUploading={isUploading}
            onUploadingChange={onUploadingChange}
          />
        </>
      );

    case STATUS_CODES.RECEIPT:
      return (
        <>
          <h3>Upload your receipt of payment for {date}</h3>
          <p> A receipt of payment confirms the total amount paid.</p>
          <a
            href="https://petcdnstore.blob.core.windows.net/csp-images/Templates/ABC_ReceiptPayment_v2.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            See an example
          </a>
          <DocTypeFileInput
            formKey={UPLOAD_DOCUMENTS_FORM_KEYS.RECEIPT}
            selectedFiles={additionalAttachementsByType[UPLOAD_DOCUMENTS_FORM_KEYS.RECEIPT]}
            isRequired={noComment && formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.RECEIPT)?.length === 0}
            requiredMessage="Please upload your receipt of payment or add a comment below."
            isUploading={isUploading}
            onUploadingChange={onUploadingChange}
          />
        </>
      );

    case STATUS_CODES.RADIOLOGY_REPORT:
      return (
        <>
          <h3>Upload your radiology results for {date}</h3>
          <p>
            This refers to the results from the x-rays performed by the Vet. We are not seeking the actual x-ray film or
            a digital copy. What we are seeking are the comments written by the veterinarian in regards to these x-rays
            or ultrasounds. You will need to contact your Vet directly to obtain these reports.
          </p>
          <a
            href="https://petcdnstore.blob.core.windows.net/csp-images/Templates/ABC_Radiology_v2.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            See an example
          </a>
          <DocTypeFileInput
            formKey={UPLOAD_DOCUMENTS_FORM_KEYS.RADIOLOGY_REPORT}
            selectedFiles={additionalAttachementsByType[UPLOAD_DOCUMENTS_FORM_KEYS.RADIOLOGY_REPORT]}
            isRequired={noComment && formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.RADIOLOGY_REPORT)?.length === 0}
            isUploading={isUploading}
            onUploadingChange={onUploadingChange}
          />
        </>
      );
    case STATUS_CODES.VET_HISTORY:
      return (
        <>
          <h3>
            Upload {petName}&apos;s vet history for {date}, or {petName}&apos;s adoption certificate
          </h3>
          <p>
            Vet history means all medical records for the life of your pet. You will need to request these from your
            vet/s, as it is generally only provided to owners at their request, as they are the vet&apos;s medical notes
            from each visit. You will need to contact each vet directly to obtain these records.
          </p>
          <a
            href="https://petcdnstore.blob.core.windows.net/csp-images/Templates/ABC_PetHistory_v2.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            See an example
          </a>
          <p>
            Note: if your pet was adopted we require <a href={CSPUrlsMap.helpCentreClaims.url}>proof of adoption</a> and
            vet history from the date of adoption.
          </p>
          <DocTypeFileInput
            formKey={UPLOAD_DOCUMENTS_FORM_KEYS.VET_HISTORY}
            selectedFiles={additionalAttachementsByType[UPLOAD_DOCUMENTS_FORM_KEYS.VET_HISTORY]}
            isRequired={noComment && formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.VET_HISTORY)?.length === 0}
            requiredMessage="Please upload your vet history or add a comment below."
            isUploading={isUploading}
            onUploadingChange={onUploadingChange}
          />
        </>
      );
    case STATUS_CODES.INVOICE:
      return (
        <>
          <h3>Upload your itemised invoice for {date}</h3>
          <p>
            A paid invoice that includes individual treatment items and has costs adding up to the total amount paid.
          </p>
          <a
            href="https://petcdnstore.blob.core.windows.net/csp-images/Templates/abc-veterinary-clinic-sample-itemised-invoice.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            See an example
          </a>
          <DocTypeFileInput
            formKey={UPLOAD_DOCUMENTS_FORM_KEYS.INVOICE}
            selectedFiles={additionalAttachementsByType[UPLOAD_DOCUMENTS_FORM_KEYS.INVOICE]}
            isRequired={noInvoice}
            requiredMessage="Please upload at least one invoice"
            isUploading={isUploading}
            onUploadingChange={onUploadingChange}
          />
        </>
      );
    case STATUS_CODES.OTHER:
      return (
        <>
          <h3>Upload your other document/s for {date}</h3>
          <DocTypeFileInput
            formKey={UPLOAD_DOCUMENTS_FORM_KEYS.OTHER}
            selectedFiles={additionalAttachementsByType[UPLOAD_DOCUMENTS_FORM_KEYS.OTHER]}
            isRequired={noComment && formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.OTHER)?.length === 0}
            isUploading={isUploading}
            onUploadingChange={onUploadingChange}
          />
        </>
      );
    case STATUS_CODES.PURCHASE_RECEIPT:
      return (
        <>
          <h3>Upload supporting documents which show when {petName} joined your family</h3>
          <p>A proof of purchase, adoption certificate or change of ownership document is required.</p>
          <a
            href="https://petcdnstore.blob.core.windows.net/csp-images/Templates/ABC_Adoption_Proof_Purchasev2.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            See an example
          </a>
          <DocTypeFileInput
            formKey={UPLOAD_DOCUMENTS_FORM_KEYS.PURCHASE_RECEIPT}
            selectedFiles={additionalAttachementsByType[UPLOAD_DOCUMENTS_FORM_KEYS.PURCHASE_RECEIPT]}
            isRequired={noComment && formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.PURCHASE_RECEIPT)?.length === 0}
            isUploading={isUploading}
            onUploadingChange={onUploadingChange}
          />
        </>
      );
    default:
      return null;
  }
};

export const mapUploadFileChecklist = (formContext, statusCode, date) => {
  const validateIfCommentEmpty = {
    required: !formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.COMMENTS)
      ? // (formContext.watch(statusCode) && !formContext.watch(`${statusCode}_CHECKLIST`))
        'Please confirm you have supplied this documentation'
      : ''
  };
  switch (statusCode) {
    case STATUS_CODES.CLIENT_CORRESPONDENCE:
      return (
        <CheckBoxInput
          name={UPLOAD_DOCUMENTS_FORM_KEYS.CLIENT_CORRESPONDENCE_CHECKLIST}
          label={`Client correspondence for ${date}.`}
          fieldFormContext={formContext}
          validation={validateIfCommentEmpty}
        />
      );
    case STATUS_CODES.CLAIM_FORM:
      return (
        <CheckBoxInput
          name={UPLOAD_DOCUMENTS_FORM_KEYS.CLAIM_FORM_CHECKLIST}
          label={`Claim form for ${date}`}
          fieldFormContext={formContext}
          validation={validateIfCommentEmpty}
        />
      );

    case STATUS_CODES.VET_CONSULTATION_NOTES:
      return (
        <CheckBoxInput
          name={UPLOAD_DOCUMENTS_FORM_KEYS.VET_CONSULTATION_NOTES_CHECKLIST}
          label={`Consultation notes for ${date}`}
          fieldFormContext={formContext}
          validation={validateIfCommentEmpty}
        />
      );

    case STATUS_CODES.PATHOLOGY_REPORT:
      return (
        <CheckBoxInput
          name={UPLOAD_DOCUMENTS_FORM_KEYS.PATHOLOGY_REPORT_CHECKLIST}
          label={`Pathology for ${date}`}
          fieldFormContext={formContext}
          validation={validateIfCommentEmpty}
        />
      );

    case STATUS_CODES.PRE_AUTHORIZATION:
      return (
        <CheckBoxInput
          name={UPLOAD_DOCUMENTS_FORM_KEYS.PRE_AUTHORIZATION_CHECKLIST}
          label={`Pre-authorisation form for ${date}`}
          fieldFormContext={formContext}
          validation={validateIfCommentEmpty}
        />
      );

    case STATUS_CODES.RECEIPT:
      return (
        <CheckBoxInput
          name={UPLOAD_DOCUMENTS_FORM_KEYS.RECEIPT_CHECKLIST}
          label={`Receipt of payment for ${date}`}
          fieldFormContext={formContext}
          validation={validateIfCommentEmpty}
        />
      );

    case STATUS_CODES.RADIOLOGY_REPORT:
      return (
        <CheckBoxInput
          name={UPLOAD_DOCUMENTS_FORM_KEYS.RADIOLOGY_REPORT_CHECKLIST}
          label={`Radiology results for ${date}`}
          fieldFormContext={formContext}
          validation={validateIfCommentEmpty}
        />
      );

    case STATUS_CODES.VET_HISTORY:
      return (
        <CheckBoxInput
          name={UPLOAD_DOCUMENTS_FORM_KEYS.VET_HISTORY_CHECKLIST}
          label={`Vet history for ${date}`}
          fieldFormContext={formContext}
          validation={validateIfCommentEmpty}
        />
      );

    case STATUS_CODES.INVOICE:
      // Invoice file and checklist are mandatory, so we need to check if the file is uploaded or the checkbox is checked
      // eslint-disable-next-line no-case-declarations
      const hasInvoiceFileOrInvoiceChecked =
        formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.INVOICE_CHECKLIST) &&
        formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.INVOICE) &&
        formContext.watch(UPLOAD_DOCUMENTS_FORM_KEYS.INVOICE).length > 0;
      // eslint-disable-next-line no-case-declarations
      const validateInvoiceMandatory = {
        required: !hasInvoiceFileOrInvoiceChecked ? 'Please confirm you have supplied this documentation' : ''
      };
      return (
        <CheckBoxInput
          name={UPLOAD_DOCUMENTS_FORM_KEYS.INVOICE_CHECKLIST}
          label={`Invoice for ${date} (mandatory)`}
          fieldFormContext={formContext}
          validation={validateInvoiceMandatory}
        />
      );
    case STATUS_CODES.OTHER:
      return (
        <CheckBoxInput
          name={UPLOAD_DOCUMENTS_FORM_KEYS.OTHER_CHECKLIST}
          label={`Other documents for ${date}`}
          fieldFormContext={formContext}
          validation={validateIfCommentEmpty}
        />
      );

    case STATUS_CODES.PURCHASE_RECEIPT:
      return (
        <CheckBoxInput
          name={UPLOAD_DOCUMENTS_FORM_KEYS.PURCHASE_RECEIPT_CHECKLIST}
          label="Proof of purchase"
          fieldFormContext={formContext}
          validation={validateIfCommentEmpty}
        />
      );

    default:
      return null;
  }
};
